import React from 'react';
import { motion } from 'framer-motion';

// Define the interface for a single card's data
interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// Reusable FeatureCard component
const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  return (
    <motion.div className="group relative w-full bg-white rounded-2xl shadow-md p-4 transition-all duration-500 max-md:max-w-md max-md:mx-auto md:w-2/5 md:h-64 xl:p-7 xl:w-1/4 hover:bg-primary-600 hover:shadow-xl transition">
      <div className="bg-background rounded-full flex justify-center items-center mb-5 w-14 h-14 group-hover:bg-primary-100 transition-all duration-500">
        {icon}
      </div>
      <h4 className="text-xl font-semibold text-primary mb-3 capitalize transition-all duration-500 group-hover:text-white">
        {title}
      </h4>
      <p className="text-sm font-normal text-primary-800 transition-all duration-500 leading-5 group-hover:text-white">
        {description}
      </p>
    </motion.div>
  );
};

// Define the interface for the main component props
interface WhyChooseUsProps {
  title?: string;
  description?: string;
  cards: Array<{
    icon: React.ReactNode;
    title: string;
    description: string;
  }>;
}

// Main WhyChooseUs component
const WhyChooseUs: React.FC<WhyChooseUsProps> = ({
  title = 'Why Choose Us',
  description = 'We are committed to providing an exceptional online book shopping experience. Our vast collection, fast shipping, and customer-first approach set us apart in the industry.',
  cards,
}) => {
  return (
    <section className="py-24 bg-background-soft/50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-10 lg:mb-16 flex justify-center items-center flex-col gap-x-0 gap-y-6 lg:gap-y-0 lg:flex-row lg:justify-between max-md:max-w-lg max-md:mx-auto">
          <div className="relative w-full text-center lg:text-left lg:w-2/4">
            <h2 className="text-4xl font-bold text-primary leading-[3.25rem] lg:mb-6 mx-auto max-w-max lg:max-w-md lg:mx-0">
              {title}
            </h2>
          </div>
          <div className="relative w-full text-center lg:text-left lg:w-2/4">
            <p className="text-lg font-normal text-primary-800 mb-5">
              {description}
            </p>
          </div>
        </div>
        <div className="flex justify-center items-center gap-x-5 gap-y-8 lg:gap-y-0 flex-wrap md:flex-wrap lg:flex-nowrap lg:flex-row lg:justify-between lg:gap-x-8">
          {cards.map((card, index) => (
            <FeatureCard
              key={index}
              icon={card.icon}
              title={card.title}
              description={card.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;
export { FeatureCard };